import PropTypes from 'prop-types';
import hljs from 'highlight.js';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '@/components/iconify';
import { useNotify } from '@/components/snackbar';
import '@/utils/highlight';

const stringify = (value) => {
  if (value == null || value === '') {
    return '{}';
  }

  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch (_error) {
      return value;
    }
  }

  return JSON.stringify(value, null, 2);
};

const highlightJson = (value) => {
  const content = stringify(value);

  try {
    return hljs.highlight(content, { language: 'json' }).value;
  } catch (_error) {
    return content;
  }
};

PayloadBlock.propTypes = {
  label: PropTypes.string.isRequired,
  subject: PropTypes.string.isRequired,
  copiedMessage: PropTypes.string.isRequired,
  value: PropTypes.any,
  maxHeight: PropTypes.number.isRequired,
  testId: PropTypes.string.isRequired,
  onCopy: PropTypes.func.isRequired,
};

function PayloadBlock({ label, subject, copiedMessage, value, maxHeight, testId, onCopy }) {
  // Sem valor a tela mostra "{}" e não há o que copiar; um objeto vazio real continua copiável.
  const isEmpty = value == null || value === '';

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="caption">{label}:</Typography>
        <Tooltip title="Copiar">
          <span>
            <IconButton
              size="small"
              color="inherit"
              aria-label={`Copiar ${subject}`}
              data-testid={testId}
              disabled={isEmpty}
              onClick={() => onCopy(value, subject, copiedMessage)}
            >
              <Iconify icon="eva:copy-fill" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Box
        component="pre"
        sx={{
          m: 0,
          maxHeight,
          overflow: 'auto',
          borderRadius: 1.5,
          bgcolor: '#2E3741',
          p: 1.5,
          fontFamily: 'Monospace',
          fontSize: 12,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          '& .hljs': { background: 'transparent', padding: 0 },
        }}
      >
        <Box component="code" dangerouslySetInnerHTML={{ __html: highlightJson(value) }} />
      </Box>
    </Box>
  );
}

WebhookExecutionDetailsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onResend: PropTypes.func,
  isResending: PropTypes.bool,
  execution: PropTypes.shape({
    eventName: PropTypes.string,
    identifier: PropTypes.string,
    sentAtLabel: PropTypes.string,
    timeline: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        time: PropTypes.string,
        description: PropTypes.string,
      })
    ),
    requestHeaders: PropTypes.any,
    requestBody: PropTypes.any,
    responseStatus: PropTypes.string,
    responseTime: PropTypes.string,
    responseTimeLabel: PropTypes.string,
    responseBody: PropTypes.any,
  }),
};

export default function WebhookExecutionDetailsModal({
  open,
  onClose,
  onResend,
  isResending,
  execution,
}) {
  const {
    eventName,
    identifier,
    sentAtLabel,
    timeline = [],
    requestHeaders,
    requestBody,
    responseStatus,
    responseTime,
    responseTimeLabel,
    responseBody,
  } = execution || {};

  const notify = useNotify();

  const handleCopy = async (value, subject, copiedMessage) => {
    try {
      await navigator.clipboard.writeText(stringify(value));
      notify.success(copiedMessage);
    } catch (_error) {
      notify.error(`Falha ao copiar ${subject}.`);
    }
  };

  // O Accordion é um Paper e o Paper aplica text.primary, cortando o branco herdado do
  // Dialog, que é escuro nos dois temas. O override global pinta background.paper quando
  // expandido, e as duas classes de '&.Mui-expanded' vencem um bgcolor simples no sx.
  const accordionSx = {
    bgcolor: 'transparent',
    color: 'inherit',
    boxShadow: 'none',
    '&:before': { display: 'none' },
    '&.Mui-expanded': {
      bgcolor: 'transparent',
      boxShadow: 'none',
    },
  };

  const accordionSummarySx = {
    bgcolor: '#333E49',
    borderRadius: '8px',
    px: 2,
    minHeight: 48,
    '&.Mui-expanded': {
      borderRadius: '8px 8px 0 0',
    },
    '& .MuiAccordionSummary-content': {
      my: 0,
    },
  };

  const accordionDetailsSx = {
    bgcolor: (theme) => theme.palette.grey[800],
    borderRadius: 2,
    mt: 1,
    p: 2,
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: (theme) => theme.palette.grey[900],
          color: (theme) => theme.palette.common.white,
          borderRadius: 3,
        },
      }}
    >
      <DialogContent sx={{ py: 2.5 }}>
        <Stack gap={2.5}>
          <Stack gap={0.5}>
            <Typography variant="h6">
              {identifier ? `${eventName} • ${identifier}` : eventName}
            </Typography>
            {sentAtLabel && (
              <Typography
                variant="body2"
                sx={{ opacity: 0.7 }}
              >
                Enviado em {sentAtLabel}
              </Typography>
            )}
          </Stack>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />

          <Accordion
            defaultExpanded
            disableGutters
            square
            sx={accordionSx}
          >
            <AccordionSummary
              expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}
              sx={accordionSummarySx}
            >
              <Typography variant="subtitle2">Timeline</Typography>
            </AccordionSummary>
            <AccordionDetails sx={accordionDetailsSx}>
              <Box
                sx={{
                  borderRadius: 2,
                  border: (theme) => `1px solid ${theme.palette.grey[700]}`,
                  overflow: 'hidden',
                }}
              >
                {timeline.map((item, index) => (
                  <Box
                    key={`${item.time}-${index}`}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      px: 2,
                      py: 1.25,
                      bgcolor: (theme) =>
                        index % 2 === 0 ? theme.palette.grey[800] : theme.palette.grey[850],
                    }}
                  >
                    <Typography variant="body2">{item.time}</Typography>
                    <Typography variant="body2" sx={{ ml: 2, textAlign: 'right' }}>
                      {item.description}
                    </Typography>
                  </Box>
                ))}
                {timeline.length === 0 && (
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="body2">
                      Nenhuma informação de timeline disponível.
                    </Typography>
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion
            disableGutters
            square
            sx={accordionSx}
          >
            <AccordionSummary
              expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}
              sx={accordionSummarySx}
            >
              <Typography variant="subtitle2">Requisição enviada</Typography>
            </AccordionSummary>
            <AccordionDetails sx={accordionDetailsSx}>
              <Box
                sx={{
                  borderRadius: 2,
                  bgcolor: '#2E3741',
                  p: 2,
                }}
              >
                <Stack gap={1.5}>
                  <PayloadBlock
                    label="Headers"
                    subject="headers da requisição"
                    copiedMessage="Headers copiados para a área de transferência!"
                    value={requestHeaders}
                    maxHeight={200}
                    testId="copy-request-headers"
                    onCopy={handleCopy}
                  />
                  <PayloadBlock
                    label="Body"
                    subject="body da requisição"
                    copiedMessage="Body copiado para a área de transferência!"
                    value={requestBody}
                    maxHeight={260}
                    testId="copy-request-body"
                    onCopy={handleCopy}
                  />
                </Stack>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion
            disableGutters
            square
            sx={accordionSx}
          >
            <AccordionSummary
              expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}
              sx={accordionSummarySx}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="subtitle2">Resposta recebida</Typography>
                  {responseTime && (
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                          em {responseTime}
                      </Typography>
                  )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={accordionDetailsSx}>
              <Stack gap={1.5}>
                  {responseStatus && (
                    <Stack gap={0.5} direction='row'>
                      <Typography variant="caption">Status: </Typography>
                      <Typography variant="caption" color='#CACCCF'>{responseStatus}</Typography>
                    </Stack>
                  )}
                  {responseTimeLabel && (
                    <Stack gap={0.5} direction='row'>
                      <Typography variant="caption">Tempo de resposta: </Typography>
                      <Typography variant="caption" color='#CACCCF'>{responseTimeLabel}</Typography>
                    </Stack>
                  )}

                <Box
                  sx={{
                    mt: 1.5,
                    borderRadius: 2,
                    bgcolor: '#2E3741',
                    p: 2,
                  }}
                >
                  <PayloadBlock
                    label="Body"
                    subject="body da resposta"
                    copiedMessage="Body copiado para a área de transferência!"
                    value={responseBody}
                    maxHeight={260}
                    testId="copy-response-body"
                    onCopy={handleCopy}
                  />
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button variant="outlined" onClick={onClose} color="inherit">
          Fechar
        </Button>
        <LoadingButton variant="contained" onClick={onResend} loading={isResending}>
          Reenviar Este Webhook
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
